import { useState, useEffect, useRef } from 'react';
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/AuthContext";
import { useCreateTask } from "@/context/CreateTaskContext";

export function CreateTaskFromSubmissionDialog({
    open,
    onOpenChange,
    submission,
    formSlug,
    onTaskCreated
}) {
    const { toast } = useToast();
    const { axiosInstance } = useAuth();
    const { openCreateTaskDialog, isOpen: isContextDialogOpen } = useCreateTask();

    const [hasOpenedDialog, setHasOpenedDialog] = useState(false);
    const wasContextDialogOpen = useRef(false);

    // Reset state when context dialog closes (handles X button, Escape, etc.)
    useEffect(() => {
        if (wasContextDialogOpen.current && !isContextDialogOpen && hasOpenedDialog) {
            // Dialog was open and now closed - reset our state
            setHasOpenedDialog(false);
            onOpenChange(false);
        }
        wasContextDialogOpen.current = isContextDialogOpen;
    }, [isContextDialogOpen, hasOpenedDialog, onOpenChange]);

    // Open dialog immediately and generate AI in background
    useEffect(() => {
        if (open && formSlug && submission && !hasOpenedDialog) {
            openDialogAndGenerateAI();
        } else if (!open) {
            setHasOpenedDialog(false);
        }
    }, [open, formSlug, submission, hasOpenedDialog]);

    const openDialogAndGenerateAI = async () => {
        try {
            // Fetch projects from the form's organization
            const projectsResponse = await axiosInstance.get(`/forms/${formSlug}/projects`);
            const formProjects = projectsResponse.data;

            if (!formProjects || formProjects.length === 0) {
                toast({
                    title: "Error",
                    description: "No projects found for this form",
                    variant: "destructive"
                });
                onOpenChange(false);
                return;
            }

            // Prepare submission data
            const submissionData = {
                submissionId: submission.id,
                createdAt: submission.createdAt
            };

            // Open dialog IMMEDIATELY with loading state
            setHasOpenedDialog(true);
            openCreateTaskDialog({
                project: formProjects[0],
                generatingTask: true, // Show loading state
                createdFromSubmission: submissionData,
                onSuccess: (task) => {
                    onOpenChange(false);
                    if (onTaskCreated) {
                        onTaskCreated({
                            ...task,
                            linkedToSubmission: true
                        });
                    }
                }
            });

            // Generate AI task suggestion in background
            await generateAITaskSuggestion(formProjects, submissionData);
        } catch (error) {
            console.error('Error in AI generation flow:', error);
            toast({
                title: "Error",
                description: "Failed to load projects",
                variant: "destructive"
            });
            onOpenChange(false);
        }
    };

    const generateAITaskSuggestion = async (formProjects, submissionData) => {
        try {
            // Call the AI endpoint to generate task suggestion
            const response = await axiosInstance.post(
                `/forms/submissions/${submission.id}/generate-task`,
                {
                    submissionData: submission.data
                }
            );

            // Make sure we have summary and description
            if (response.data.summary && response.data.description) {
                // Extract files from submission data to show as preloaded attachments
                const submissionFiles = [];
                if (submission.data) {
                    Object.values(submission.data).forEach(value => {
                        if (Array.isArray(value)) {
                            // Check if this looks like file data
                            value.forEach(item => {
                                if (item && typeof item === 'object' && item.filename && item.key) {
                                    submissionFiles.push({
                                        id: item.key,
                                        name: item.filename,
                                        size: item.size || 0,
                                        type: item.contentType || 'application/octet-stream',
                                        file: null, // No actual File object since it's already uploaded
                                        alt: item.filename,
                                        url: null, // Will be generated when needed
                                        key: item.key,
                                        filename: item.filename,
                                        contentType: item.contentType,
                                        fromSubmission: true // Flag to indicate this came from submission
                                    });
                                }
                            });
                        }
                    });
                }

                // Update the already-open dialog with AI-generated data
                openCreateTaskDialog({
                    project: formProjects[0], // Default to first project
                    summary: response.data.summary,
                    description: response.data.description,
                    submissionFiles: submissionFiles,
                    generatingTask: false, // AI generation is done
                    createdFromSubmission: submissionData,
                    onSuccess: (task) => {
                        onOpenChange(false);
                        if (onTaskCreated) {
                            onTaskCreated({
                                ...task,
                                linkedToSubmission: true
                            });
                        }
                    }
                });
            } else {
                console.error('Failed to generate task suggestion:', response);
                toast({
                    description: "Failed to generate task suggestion",
                    variant: "destructive"
                });
                onOpenChange(false);
            }
        } catch (error) {
            console.error('Error generating AI task suggestion:', error);
            toast({
                description: "Failed to generate task suggestion",
                variant: "destructive"
            });
            onOpenChange(false);
        }
    };

    // This component now just triggers the context dialog
    // Rendering is handled by CreateTaskContext
    return null;
} 